'use client';

import React, { ReactNode, useState } from 'react';
import { Typography } from 'antd';
import { FiAlertTriangle, FiMapPin, FiPhone, FiMail, FiCopy, FiCheck } from 'react-icons/fi';
import { renderAlertMessage } from '@/utils/errorHandling';
import { isInvalidDocumentError } from '@/app/(main)/documents/verify/utils';

const { Title, Text } = Typography;

interface ContactDetail {
  id: string;
  label: string;
  value: ReactNode;
}

interface ErrorCardProps {
  error: string;
  safeErrorMessage: string;
  documentId?: string;
  contactDetails?: ContactDetail[];
}

const ErrorCard = ({ error, safeErrorMessage, documentId, contactDetails }: ErrorCardProps) => {
  const invalidDocument = isInvalidDocumentError(error);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!documentId) return;
    navigator.clipboard.writeText(documentId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      style={{
        width: '100%',
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 860,
          margin: '0 auto',
          background: '#ffffff',
          border: '1px solid #e8c4c4',
  
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 6,
            background:
              'linear-gradient(90deg, #c0392b 0%, #e57373 50%, #c0392b 100%)',
          }}
        />

        {(
          ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const
        ).map((corner) => {
          const isTop = corner.includes('top');
          const isLeft = corner.includes('left');
          return (
            <div
              key={corner}
              style={{
                position: 'absolute',
                top: isTop ? 10 : undefined,
                bottom: !isTop ? 10 : undefined,
                left: isLeft ? 10 : undefined,
                right: !isLeft ? 10 : undefined,
                width: 36,
                height: 36,
                borderTop: isTop ? '2px solid #c0392b' : undefined,
                borderBottom: !isTop ? '2px solid #c0392b' : undefined,
                borderLeft: isLeft ? '2px solid #c0392b' : undefined,
                borderRight: !isLeft ? '2px solid #c0392b' : undefined,
                pointerEvents: 'none',
                opacity: 0.4,
              }}
            />
          );
        })}

        <div
          className="ec-header"
          style={{
            background: '#fdf5f5',
            borderBottom: '1px solid #e8c4c4',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#c0392b',
              marginBottom: 18,
            }}
          >
            Official Documents Verification
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              <Title
                level={2}
                className="mb-2! text-[24px]! sm:text-[28px]!"
                style={{ color: '#0f172a' }}
              >
                {invalidDocument ? 'Document Not Found' : 'Verification Failed'}
              </Title>
              <Text
                style={{
                  fontSize: 14,
                  color: '#475569',
                  lineHeight: 1.7,
                  fontStyle: 'italic',
                }}
              >
                {invalidDocument
                  ? 'This document could not be verified as authentic in our system.'
                  : 'We could not validate this document at this time.'}
              </Text>
            </div>

            <div
              style={{
                width: 116,
                height: 116,
                borderRadius: '50%',
                border: '2px solid #c0392b',
                background: '#fff5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                flexShrink: 0,
                gap: 6,
              }}
            >
              <FiAlertTriangle size={28} color="#c0392b" />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#c0392b',
                }}
              >
                {invalidDocument ? 'Not Found' : 'Failed'}
              </span>
            </div>
          </div>
        </div>

        <div className="ec-body" style={{ background: '#ffffff' }}>
          <div
            style={{
              border: '1px solid #e8c4c4',
              background: '#fdf5f5',
              padding: '14px 20px',
              marginBottom: 28,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <FiAlertTriangle
              size={16}
              color="#c0392b"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#7b1a1a',
                  marginBottom: 3,
                }}
              >
                {invalidDocument
                  ? 'Status: Not Found'
                  : 'Status: Verification Error'}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#475569',
                  fontStyle: 'italic',
                  lineHeight: 1.6,
                }}
              >
                {invalidDocument
                  ? 'The document referenced does not exist in our records. It may be invalid, forged, or the number entered may be incorrect.'
                  : renderAlertMessage(error)}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              marginBottom: 28,
            }}
          >
            <div className="ec-row-2">
              <div className="ec-info-block">
                <div className="ec-info-label">Document Number Queried</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    className="ec-info-value"
                    style={{ flex: 1, wordBreak: 'break-all' }}
                  >
                    {documentId || '—'}
                  </div>
                  {documentId && (
                    <button
                      onClick={handleCopy}
                      title={copied ? 'Copied!' : 'Copy document ID'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: copied ? '#1a4a1a' : '#c0392b',
                        border: `1px solid ${copied ? '#b7d4b7' : '#e8c4c4'}`,
                        background: copied ? '#f6faf6' : '#fff5f5',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.15s',
                      }}
                    >
                      {copied ? (
                        <>
                          <FiCheck size={11} /> Copied
                        </>
                      ) : (
                        <>
                          <FiCopy size={11} /> Copy
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="ec-info-block">
                <div className="ec-info-label">Error Detail</div>
                <div className="ec-info-value" style={{ color: '#7b1a1a' }}>
                  {!invalidDocument
                    ? safeErrorMessage
                    : 'No matching record found for the provided document identifier.'}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
              paddingBottom: 10,
              borderBottom: '1px solid #e8c4c4',
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#c0392b',
              }}
            >
              What You Can Do
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 16,
              marginBottom: 28,
            }}
          >
            <div
              style={{
                border: '1px solid #e8c4c4',
                background: '#fdf5f5',
                padding: '16px 18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <FiPhone size={15} color="#c0392b" style={{ flexShrink: 0 }} />
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#7b1a1a',
                  }}
                >
                  Call Customer Care
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                Reach our support line during business hours (Mon–Fri, 8am–5pm)
                to report a discrepancy or request manual verification.
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#c0392b',
                }}
              >
                {contactDetails?.find((d) => d.id === 'tel')?.value ?? '—'}
              </div>
            </div>

            <div
              style={{
                border: '1px solid #e8c4c4',
                background: '#fdf5f5',
                padding: '16px 18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <FiMail size={15} color="#c0392b" style={{ flexShrink: 0 }} />
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#7b1a1a',
                  }}
                >
                  Email Support
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                Send us the document number and any supporting information and
                our team will investigate and respond within 2 business days.
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#c0392b',
                }}
              >
                {contactDetails?.find((d) => d.id === 'email')?.value ?? '—'}
              </div>
            </div>

            <div
              style={{
                border: '1px solid #e8c4c4',
                background: '#fdf5f5',
                padding: '16px 18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <FiMapPin size={15} color="#c0392b" style={{ flexShrink: 0 }} />
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#7b1a1a',
                  }}
                >
                  Visit a County Office
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                If you believe the document is legitimate, visit any county
                licensing or records office with the original document and a
                valid ID for in-person verification.
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: '#64748b',
                  fontStyle: 'italic',
                }}
              >
                {contactDetails?.find((d) => d.id === 'postal')?.value ??
                  'Bring the original document + national ID'}
              </div>
            </div>
          </div>

          <div
            style={{
              border: '1px solid #e8c4c4',
              background: '#fdf5f5',
              padding: '16px 20px',
              marginBottom: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#7b1a1a',
                  marginBottom: 3,
                }}
              >
                Try searching by document number
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#475569',
                  fontStyle: 'italic',
                  lineHeight: 1.6,
                }}
              >
                If you have the document number, you can search for it directly
                in our verification portal.
              </div>
            </div>
            <a
              href="/e-verify/documents"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '9px 20px',
                fontSize: 13,
                fontWeight: 700,
                color: '#c0392b',
                border: '1px solid #c0392b',
                background: '#ffffff',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Search Documents →
            </a>
          </div>

          <div
            style={{
              border: '1px solid #e8c4c4',
              background: 'rgba(192, 57, 43, 0.04)',
              padding: '18px 22px',
              marginBottom: 28,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 10,
                color: '#7b1a1a',
              }}
            >
              Important Notice
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                listStyle: 'disc',
              }}
            >
              {[
                'Double-check the document number for any typographical errors before contacting support.',
                'Do not accept this document as valid for any official transaction until verification is confirmed.',
                'If you suspect the document is fraudulent, report it to the relevant county authority immediately.',
                'Keep a copy of any correspondence with our support team for your records.',
              ].map((item, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 13,
                    color: 'rgba(0, 0, 0, 0.78)',
                    fontStyle: 'italic',
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid #e8c4c4',
            background: '#fdf5f5',
            padding: '10px 36px',
            textAlign: 'center',
          }}
        >
          <Text
            type="secondary"
            style={{ fontSize: 11 }}
          >
            This is an electronically generated verification result. For
            disputes, contact county offices directly.
          </Text>
        </div>
      </div>

      <style>{`
        .ec-header {
          padding: 32px 36px 22px;
        }

        .ec-body {
          padding: 28px 36px 32px;
        }

        .ec-row-2 {
          display: grid;
          grid-template-columns: 1fr 2fr;
          column-gap: 24px;
        }

        .ec-info-block {
          padding: 14px 0 12px;
          border-bottom: 1px solid #f0d8d8;
        }

        .ec-info-label {
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 8px;
        }

        .ec-info-value {
          font-size: 14px;
          color: #0f172a;
          line-height: 1.5;
        }

        @media (max-width: 992px) {
          .ec-header { padding: 24px 24px 18px; }
          .ec-body { padding: 20px 24px 28px; }
          .ec-row-2 { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 600px) {
          .ec-header { padding: 20px 16px 16px; }
          .ec-body { padding: 16px 16px 24px; }
          .ec-row-2 { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ErrorCard;
